<template>
  <nav
    ref="megaMenuRef"
    v-style:w="{ lg: activeL1.id ? '46.9375rem' : '23.4375rem' }"
    :aria-label="$t.primaryNavigationLabel"
    class="relative h-full flex duration-500"
    :class="{ 'mega-menu-blur-keyframe': blurAnimation }"
  >
    <!-- L1 Section -->
    <section class="f-col flex-1 overflow-auto p-4 lg:overflow-x-hidden lg:ws-nowrap" :class="{ '<lg:hidden': activeL1.id }">
      <div class="mb-6 flex c-black between">
        <base-link to="/">
          <span class="sr-only">{{ $t.brand }} {{ $t.home }}</span>
          <vf-icon height="25" src="/img/logos/timberland/full.svg" width="144" />
        </base-link>
        <base-button
          v-if="!activeL1?.id"
          :aria-label="$t.close"
          class="p-2 -m-2"
          data-test-id="vf-dialog-close"
          @click="emit('close')"
        >
          <vf-icon name="close" size="md" />
        </base-button>
      </div>

      <section class="relative b-b b-brand-1/20 px-4 pb-4 text-sm c-brand-4">
        <div class="flex items-center between -mx-4">
          <div>
            <div class="subtitle-4 c-brand-4">
              {{ greeting }}
            </div>
            <div v-if="auth.loggedIn">
              <span v-if="$feature.enableLoyaltyFeatures">
                <template v-if="user.isLoyaltyMember">
                  <template
                    v-if="!loyalty.vouchersPending
                      && !loyalty.vouchers?.applicableLoyaltyVouchers?.length && user.loyaltyPointsBalance"
                  >
                    {{ user.loyaltyPointsBalance }} {{ $pluralize($t.points, user.loyaltyPointsBalance) }}
                  </template>
                  <template v-else-if="loyalty.vouchers?.applicableLoyaltyVouchers?.[0]?.available">
                    {{ replaceAll($t.loyaltyRewardsDetails.rewardAmount, { reward: loyalty.currentReward }) }}
                  </template>
                </template>
                <span v-else>{{ $t.joinLoyaltyCta }}</span>
              </span>
            </div>
            <base-interpolator v-else :content="$t.signInOrCreateAccount">
              <template #sign-in="{ args: [message] }">
                <base-button
                  class="underlined"
                  data-test-id="checkout-sign-in-button"
                  @click="DialogSignIn.open({ formLocation: 'modal:single:checkout:none' })"
                >
                  {{ message }}
                </base-button>
              </template>
              <template #join-now="{ args: [message] }">
                <base-button
                  class="underlined"
                  data-test-id="checkout-sign-up-button"
                  @click="DialogSignUp.open({ formLocation: 'modal:single:checkout:none' })"
                >
                  {{ message }}
                </base-button>
              </template>
            </base-interpolator>
          </div>
          <template v-if="auth.loggedIn">
            <base-link v-if="user.isLoyaltyMember" :aria-label="$t.myAccount" to="/account/loyalty">
              <vf-icon class="c-brand-4" name="account" size="md" />
              <span class="absolute-0" />
            </base-link>
            <base-button
              v-else
              :aria-label="$t.joinLoyaltyCta"
              @click="DialogLoyaltySignUp.open({ formLocation: 'modal:single:myaccount:none' })"
            >
              <vf-icon class="c-brand-4" name="account" size="md" />
              <span class="absolute-0" />
            </base-button>
          </template>
          <vf-icon v-else class="c-brand-4" name="account" size="md" />
        </div>
      </section>

      <transition-group class="f-col grow -mx-4" name="stagger-slide" tag="ul">
        <li
          v-for="(l1, i) in items.children"
          :key="l1.id || i"
          :ref="el => l1Refs[l1.id] = el"
          class="@hover:bg-brand-1/10"
          :class="{ '!bg-brand-1/20': l1.id === activeL1?.id }"
          :style="getStaggerStyle(i, 'x')"
        >
          <component
            :is="l1.columns?.length ? BaseButton : BaseLink"
            class="w-full flex items-center gap-2 px-4 py-3 subtitle-4 between"
            :href="l1.target?.url"
            rel="noopener nofollow"
            @click="handleL1Click(l1)"
          >
            <div class="flex items-center gap-x-1">
              <vf-icon
                v-if="l1.menuIcon"
                class="pointer-events-none"
                height="26"
                :src="`/img/logos/${brand}/${l1.menuIcon.icon}.svg`"
                width="26"
              />

              {{ l1.title }}
            </div>
            <vf-icon v-if="l1.columns?.length" name="chevron" size="sm" />
          </component>
        </li>
      </transition-group>

      <vf-utility-navigation
        v-if="links"
        class="mt-4"
        :links
        @click="emit('close')"
        @sign-in="emit('signIn')"
      />
    </section>

    <!-- L2/L3 Sections -->
    <section class="flex-1 overflow-auto p-4 lg:b-l lg:b-brand-4/20" :class="{ hidden: !activeL1.id }">
      <div class="mb-6 flex c-black between">
        <div>
          <base-button class="flex items-center -ml-2 lg:hidden" @click="handleBackClick">
            <vf-icon dir="left" name="chevron" size="md" />
            <span class="sr-only">{{ $t.backTo }}</span>
            <span class="body-2" style="margin-top: -0.0625rem">{{ activeL1.title }}</span>
          </base-button>
        </div>
        <base-button
          :aria-label="$t.close"
          class="p-2 -m-2"
          data-test-id="vf-dialog-close"
          @click="$viewport.lg ? handleBackClick() : emit('close')"
        >
          <vf-icon name="close" size="md" />
        </base-button>
      </div>

      <ul class="<lg:-mx-4">
        <li v-for="(l1, i) in items.children" :key="i">
          <div v-show="l1.id === activeL1?.id" :id="l1.id" class="slide-left w-full">
            <template v-if="l1.columns?.length">
              <div
                :key="`${l1.id}-viewAll`"
                class="b-b b-brand-1/20"
                :class="{ '<lg:hidden': l1.hideShopAll?.sm, 'lg:hidden': l1.hideShopAll?.lg }"
                :style="getStaggerStyle(0, 'y')"
              >
                <base-link
                  class="w-full flex items-center py-3 between <lg:px-4"
                  :href="l1.target?.url"
                  rel="noopener nofollow"
                  @click="handleL1ViewAllClick(l1)"
                >
                  <span class="subtitle-4">{{ l1.overrideShopAll || `${$t.viewAll} ${l1.title}` }}</span>
                </base-link>
              </div>
              <template v-for="(column, colKey) in l1.columns" :key="colKey">
                <div
                  v-for="(l2, l2Key) in column.children"
                  :key="l2.id || l2Key"
                  class="b-b b-brand-1/20"
                  :style="getStaggerStyle(l2Key, 'y')"
                >
                  <!-- L2 Header -->
                  <component
                    :is="l2.target ? 'a' : 'button'"
                    :ref="el => l2Refs[l2.id] = el"
                    :aria-controls="l2.children?.length ? `l3-content-${l2.id}` : undefined"
                    :aria-expanded="l2.children?.length ? expandedL2.includes(l2.id) : undefined"
                    class="w-full flex items-center py-3 between <lg:px-4"
                    :href="l2.target?.url"
                    rel="noopener nofollow"
                    @click="handleL2Click(l2, $event)"
                  >
                    <span class="subtitle-4">{{ l2.title }}</span>
                    <vf-icon
                      v-if="l2.children?.length"
                      :name="expandedL2.includes(l2.id) ? 'minus' : 'plus'"
                      size="sm"
                    />
                  </component>

                  <!-- L3 Collapsible Content -->
                  <transition name="collapse">
                    <transition-group
                      v-if="l2.children?.length && expandedL2.includes(l2.id)"
                      :id="`l3-content-${l2.id}`"
                      :aria-labelledby="`l2-header-${l2.id}`"
                      class="body-3 pb-3 duration-500 space-y-3 <lg:px-4"
                      name="stagger-slide"
                      tag="ul"
                    >
                      <!-- View All Option -->
                      <li :class="{ '<lg:hidden': l2.hideShopAll?.sm, 'lg:hidden': l2.hideShopAll?.lg }" :style="getStaggerStyle(0, 'x')">
                        <base-link class="group block py-2" :to="l2.target?.url" @click="handleL2ViewAllClick(l2)">
                          <span class="underscore">{{ l2.overrideShopAll || `${$t.viewAll} ${l2.title}` }}</span>
                        </base-link>
                      </li>

                      <!-- L3 Items -->
                      <li
                        v-for="(l3, l3Key) in l2.children"
                        :key="l3.id || l3Key"
                        :style="getStaggerStyle(l3Key + 1, 'x')"
                      >
                        <base-link class="group block py-2" :to="l3.target?.url" @click="handleL3Click(l2, l3)">
                          <span class="underscore">{{ l3.title }}</span>
                        </base-link>
                      </li>
                    </transition-group>
                  </transition>
                </div>
              </template>
            </template>
          </div>
        </li>
      </ul>
    </section>
  </nav>
</template>

<script lang="ts" setup>
import type { MegaMenuContent, MenuItem } from '#types/components/cms/mega-menu'
import type { NavigationLink } from '#types/navigation'
import type { RecursivePartial } from '#types/utils'
import { BaseButton, BaseLink } from '#components'

const { items } = defineProps<{
  items: MegaMenuContent
  links?: NavigationLink[]
}>()

const emit = defineEmits<{
  close: []
  signIn: []
}>()

const { brand } = useRuntimeConfig().public

const { DialogSignIn, DialogSignUp, DialogLoyaltySignUp } = useDialogsStore()

const auth = useAuthStore()
const user = useUserStore()

const loyalty = useLoyaltyStore()
const { $t, $gtm, $viewport, $feature } = useNuxtApp()
const { details } = storeToRefs(useUserStore())

const activeL1 = ref<RecursivePartial<MenuItem>>({})
const expandedL2 = ref<string[]>([])
const l1Refs = ref({})
const l2Refs = ref({})
const megaMenuRef = ref<HTMLElement>()

const greeting = computed(() => {
  if (auth.loggedIn) {
    return details.value?.firstName
      ? replaceAll($t.loyaltyRewardsDetails.greetingWithName, { name: details.value.firstName })
      : $t.loyaltyRewardsDetails.greeting
  }

  if ($feature.enableLoyaltyFeatures) {
    return $t.joinLoyaltyCta
  }

  return $t.signIn
})

const getStaggerStyle = (index: number, direction: 'x' | 'y' = 'x') => {
  const delay = index * 50
  const transform = direction === 'x' ? 'translateX(-30px)' : 'translateY(-20px)'

  return {
    '--stagger-delay': `${delay}ms`,
    'opacity': '0',
    transform,
    'animation': 'stagger-fade-in 0.6s ease forwards',
    'animationDelay': `${delay}ms`
  }
}

const getClickEventLabel = (...args: RecursivePartial<MenuItem>[]) => args.map((mi) => mi.delocalizedTracking || mi.name).join(' | ')

const focusL1Element = (id: string) => nextTick(() => l1Refs.value[id]?.firstElementChild?.focus())

const focusL2Element = (id: string) => nextTick(() => l2Refs.value[id]?.focus())

const handleBackClick = () => {
  const l1 = activeL1.value
  activeL1.value = {}
  if (l1.id) focusL1Element(l1.id)
}

const handleL1ViewAllClick = (l1: MenuItem) => {
  $gtm.push('topNav.onClickTopNavLink', getClickEventLabel(l1))
  emit('close')
}

const handleL2ViewAllClick = (l2: MenuItem) => {
  $gtm.push('topNav.onClickTopNavLink', getClickEventLabel(activeL1.value, l2))
  emit('close')
}

const handleL1Click = (l1: MenuItem) => {
  if (!l1?.columns?.length) {
    if (l1?.target?.url) {
      $gtm.push('topNav.onClickTopNavLink', getClickEventLabel(l1))
    }
    return
  }

  activeL1.value = l1
  expandedL2.value = []
  $gtm.push('topNav.onClickMobileMenuSection', getClickEventLabel(l1))

  nextTick(() => {
    if (l1.columns?.length && l1.columns[0]?.children?.length) {
      const firstL2 = l1.columns[0].children[0]
      if (firstL2?.id) focusL2Element(firstL2.id)
    }
  })
}

const toggleL2 = (l2: MenuItem) => {
  const index = expandedL2.value.indexOf(l2.id)
  if (index === -1)
    expandedL2.value.push(l2.id)
  else
    expandedL2.value.splice(index, 1)
}

const handleL2Click = async (l2: MenuItem, e: MouseEvent) => {
  // If L2 has children and it's not a ctrl/cmd click, prevent navigation
  if (l2.children?.length) {
    if (!(e.ctrlKey || e.metaKey)) {
      e.preventDefault()
      toggleL2(l2)
      $gtm.push('topNav.onClickMobileMenuSection', getClickEventLabel(activeL1.value, l2))
    }
    else {
      $gtm.push('topNav.onClickTopNavLink', getClickEventLabel(activeL1.value, l2))
    }
  }
  else {
    e.preventDefault()
    navigateTo(l2.target?.url)
    $gtm.push('topNav.onClickTopNavLink', getClickEventLabel(activeL1.value, l2))
  }
}

const handleL3Click = (l2: MenuItem, l3: MenuItem) => {
  $gtm.push('topNav.onClickTopNavLink', getClickEventLabel(activeL1.value, l2, l3))
  emit('close')
}

const blurAnimation = ref(false)

onMounted(() => {
  if (auth.loggedIn && !loyalty?.vouchers) loyalty.getVouchers()
  setTimeout(() => blurAnimation.value = true, 300)
})
</script>
